import { useIntl } from 'react-intl';
import useSWR from 'swr';

import { getResourceCatalogProcessesByKeys } from '../../services/resource-catalog/ResourceCatalogService';

/**
 * Shared hook to fetch a map of entity key -> display name
 * from Resource Catalog for review / definition summaries.
 */
export const useResourceCatalogNameMap = (
    tenantName: string | undefined,
    allEntityKeys: string[],
    cacheKeyPrefix: string,
): { idToNameMap: Record<string, string>; isLoadingNames: boolean } => {
    const shouldFetchNames = !!tenantName && allEntityKeys.length > 0;

    const {
        data: idToNameMap,
        isLoading,
    } = useSWR(
        shouldFetchNames
            ? [ cacheKeyPrefix, tenantName, allEntityKeys.join(',') ]
            : null,
        async () => {
            const processes = await getResourceCatalogProcessesByKeys({
                tenantName: tenantName ?? '',
                entityKeys: allEntityKeys,
            });

            const map: Record<string, string> = {};
            Object.entries(processes).forEach(([ id, process ]) => {
                map[id] = process.name;
            });

            return map;
        },
    );

    return {
        idToNameMap: idToNameMap ?? {},
        isLoadingNames: isLoading,
    };
};

/**
 * Hook that returns a function to compute tag logic display text
 * based on the operator using react-intl translations.
 */
export const useTagLogicText = () => {
    const { formatMessage: translate } = useIntl();

    return (tagOperator: string | undefined): string => {
        const operator = tagOperator ?? 'Or';
        if (operator === 'And') {
            return translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_TAGS_ALL' });
        }
        if (operator === 'None') {
            return translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_TAGS_NONE' });
        }
        return translate({ id: 'CLIENT_GOVERNANCE_POLICY_DEFINITION_TAGS_ANY' });
    };
};
